import React from 'react';
import { t, Trans } from '@lingui/macro';

import { Box, Display, Flex, Image, Link, Paragraph } from '@rover/kibble/core';
import { DSTokenMap } from '@rover/kibble/styles';
import type { CountryConfiguration } from '@rover/rsdk/src/apiClient/latest';
import type { ServiceTypeSlugEnum } from '@rover/rsdk/src/apiClient/latest/model/serviceTypeSlugEnum';
import { SeoSlugEnum } from '@rover/shared/js/constants/seo';

import iconMoney from './images/1-contact.png';
import iconGuarantee from './images/2-guarantee.png';
import iconInsurance from './images/3-protection.png';
import iconSupport from './images/4-support.png';

export type Props = {
  serviceTypeSlug: ServiceTypeSlugEnum;
  countryConfig: CountryConfiguration;
};

export type GuaranteesItem = {
  iconWidth: string;
  summaryText: React.ReactNode;
  description: React.ReactNode;
};

export type ItemsProps = {
  money: GuaranteesItem;
  insurance: GuaranteesItem;
  guarantee: GuaranteesItem;
  support?: GuaranteesItem;
};

const iconOptions = {
  guarantee: iconGuarantee,
  insurance: iconInsurance,
  money: iconMoney,
  support: iconSupport,
};

type SEO_SLUG_TO_JSX_ELEMENT = Record<
  Exclude<
    SeoSlugEnum,
    SeoSlugEnum.DOG_TRAINING | SeoSlugEnum.DOG_SITTING | SeoSlugEnum.PET_BOARDING
  >,
  JSX.Element
>;

const dogSitterReservationProtectionText = (reservationProtectionUrl: string): JSX.Element => (
  <Trans>
    It doesn't happen often, but if your dog sitter has to cancel, we’ll work with you to find a new
    one.{' '}
    <Link href={reservationProtectionUrl} target="_blank">
      Learn More{' '}
    </Link>
  </Trans>
);

const dogWalkerReservationProtectionText = (reservationProtectionUrl: string): JSX.Element => (
  <Trans>
    It doesn't happen often, but if your dog walker has to cancel, we’ll work with you to find a new
    one.{' '}
    <Link href={reservationProtectionUrl} target="_blank">
      Learn More{' '}
    </Link>
  </Trans>
);

const petSitterReservationProtectionText = (reservationProtectionUrl: string): JSX.Element => (
  <Trans>
    It doesn't happen often, but if your pet sitter has to cancel, we’ll work with you to find a new
    one.{' '}
    <Link href={reservationProtectionUrl} target="_blank">
      Learn More{' '}
    </Link>
  </Trans>
);

const catSitterReservationProtectionText = (reservationProtectionUrl: string): JSX.Element => (
  <Trans>
    It doesn't happen often, but if your cat sitter has to cancel, we’ll work with you to find a new
    one.{' '}
    <Link href={reservationProtectionUrl} target="_blank">
      Learn More{' '}
    </Link>
  </Trans>
);

export const getReservationProtectionTextForSeoSlug = (
  reservationProtectionUrl: string
): SEO_SLUG_TO_JSX_ELEMENT => ({
  [SeoSlugEnum.DOG_BOARDING]: dogSitterReservationProtectionText(reservationProtectionUrl),
  [SeoSlugEnum.DOG_WALKING]: dogWalkerReservationProtectionText(reservationProtectionUrl),
  [SeoSlugEnum.DOGGY_DAY_CARE]: dogSitterReservationProtectionText(reservationProtectionUrl),
  [SeoSlugEnum.HOUSE_SITTING]: petSitterReservationProtectionText(reservationProtectionUrl),
  [SeoSlugEnum.PET_SITTING]: petSitterReservationProtectionText(reservationProtectionUrl),
  [SeoSlugEnum.CAT_SITTING]: catSitterReservationProtectionText(reservationProtectionUrl),
});

const dogRoverProtectText = (reservationProtectionUrl: string): JSX.Element => (
  <Trans>
    Every booking includes tools to help you and your dog stay safe, like photo & message updates
    and reimbursement for eligible vet care.{' '}
    <Link href={reservationProtectionUrl} target="_blank">
      Learn More{' '}
    </Link>
  </Trans>
);

const petRoverProtectText = (reservationProtectionUrl: string): JSX.Element => (
  <Trans>
    Every booking includes tools to help you and your pet stay safe, like photo & message updates
    and reimbursement for eligible vet care.{' '}
    <Link href={reservationProtectionUrl} target="_blank">
      Learn More{' '}
    </Link>
  </Trans>
);

const catRoverProtectText = (reservationProtectionUrl: string): JSX.Element => (
  <Trans>
    Every booking includes tools to help you and your cat stay safe, like photo & message updates
    and reimbursement for eligible vet care.{' '}
    <Link href={reservationProtectionUrl} target="_blank">
      Learn More{' '}
    </Link>
  </Trans>
);

export const getRoverProtectTextForSeoSlug = (
  roverGuaranteeUrl: string
): SEO_SLUG_TO_JSX_ELEMENT => ({
  [SeoSlugEnum.DOG_BOARDING]: dogRoverProtectText(roverGuaranteeUrl),
  [SeoSlugEnum.DOG_WALKING]: dogRoverProtectText(roverGuaranteeUrl),
  [SeoSlugEnum.DOGGY_DAY_CARE]: dogRoverProtectText(roverGuaranteeUrl),
  [SeoSlugEnum.HOUSE_SITTING]: petRoverProtectText(roverGuaranteeUrl),
  [SeoSlugEnum.CAT_SITTING]: catRoverProtectText(roverGuaranteeUrl),
  [SeoSlugEnum.PET_SITTING]: petRoverProtectText(roverGuaranteeUrl),
});

const Guarantees = ({ serviceTypeSlug, countryConfig }: Props): JSX.Element => {
  const items: ItemsProps = {
    money: {
      iconWidth: '40px',
      summaryText: t`Cashless payments`,
      description: t`Book and pay through Rover’s secured platform—no cash or checks required.`,
    },
    insurance: {
      iconWidth: '34px',
      summaryText: t`Backed by RoverProtect`,
      description: getRoverProtectTextForSeoSlug(countryConfig.roverGuaranteeUrl)[serviceTypeSlug],
    },
    guarantee: {
      iconWidth: '40px',
      summaryText: t`Reservation protection`,
      description: getReservationProtectionTextForSeoSlug('/terms/reservation-protection/')[
        serviceTypeSlug
      ],
    },
    support: {
      iconWidth: '40px',
      summaryText: t`World-class support`,
      description: (
        <Trans>
          We’re friendly, knowledgeable, and total pet fanatics. You can{' '}
          <Link href={countryConfig.urls.newHelpTicketSsoRedirectUrl} target="_blank">
            get in touch with us
          </Link>{' '}
          anytime.
        </Trans>
      ),
    },
  };

  return (
    <Flex justifyContent="center" my="12x" data-qa-id="service-landing-assurance">
      <Box maxWidth={['83.3333%', '66.6666%']}>
        <Display as="h3" size="500" mb="3x" textAlign="center" responsive={false}>
          {t`Rest easy knowing we’re here for you`}
        </Display>
        <Flex as="ul" gap="8x" flexWrap="wrap" paddingLeft="0x" paddingTop="6x">
          {Object.keys(items).map((key) => {
            return (
              <Box
                key={key}
                as="li"
                flex={['0 0 100%', `1 1 calc(50% - ${DSTokenMap.SPACE_8X})`]}
                sx={{ listStyleType: 'none' }}
              >
                <Image src={iconOptions[key]} height="96px" width="96px" description="" />
                <Paragraph mt="2x" fontWeight="semibold">
                  {items[key].summaryText}
                </Paragraph>
                <Paragraph>{items[key].description}</Paragraph>
              </Box>
            );
          })}
        </Flex>
      </Box>
    </Flex>
  );
};

export default Guarantees;
